import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Check, Copy, Mail, MessageCircle, MessageSquare, X } from 'lucide-react';
import { prescriptionsService } from '@/services/prescriptions';
import { getApiError } from '@/services/api';
import { Spinner } from '@/components/ui';

interface SharePrescriptionModalProps {
  prescriptionId: string;
  isOpen: boolean;
  onClose: () => void;
  patientHasEmail?: boolean;
}

export function SharePrescriptionModal({ prescriptionId, isOpen, onClose, patientHasEmail = true }: SharePrescriptionModalProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout>>();

  const shareMutation = useMutation({
    mutationFn: () => prescriptionsService.createShareLink(prescriptionId),
  });

  const emailMutation = useMutation({
    mutationFn: () => prescriptionsService.sendEmail(prescriptionId),
  });

  const shareUrl = shareMutation.data
    ? `${window.location.origin}/p/${shareMutation.data.token}`
    : '';

  // Generate the share link the first time the modal opens (reused on later opens).
  const { data: shareData, isPending: sharePending, isError: shareError, mutate: createLink } = shareMutation;
  useEffect(() => {
    if (isOpen && !shareData && !sharePending && !shareError) {
      createLink();
    }
  }, [isOpen, shareData, sharePending, shareError, createLink]);

  // Escape to close + lock background scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const flashCopied = () => {
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      flashCopied();
    } catch {
      // Fallback for older browsers
      const input = document.querySelector<HTMLInputElement>('#share-link-input');
      if (input) {
        input.select();
        document.execCommand('copy');
        flashCopied();
      }
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(t('share.whatsappMessage', { url: shareUrl }))}`;
  const smsUrl = `sms:?body=${encodeURIComponent(t('share.smsMessage', { url: shareUrl }))}`;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        className="flex max-h-[90vh] w-full max-w-md flex-col rounded-xl bg-white shadow-xl"
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <h2 id="share-modal-title" className="card-title">{t('share.title')}</h2>
          <button type="button" onClick={onClose} className="btn-icon -me-2" aria-label={t('doctor.close')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5">
          {shareMutation.isPending && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Spinner className="h-8 w-8" />
              <p className="text-sm text-gray-500">{t('share.generateLink')}</p>
            </div>
          )}

          {shareMutation.isError && (
            <div className="alert-danger items-center justify-between" role="alert">
              <span className="break-words">{getApiError(shareMutation.error).message}</span>
              <button type="button" className="btn-secondary btn-sm" onClick={() => shareMutation.mutate()}>
                {t('doctor.retry')}
              </button>
            </div>
          )}

          {shareMutation.data && (
            <div className="space-y-4">
              <div>
                <label htmlFor="share-link-input" className="label">{t('share.link')}</label>
                <div className="flex gap-2">
                  <input
                    id="share-link-input"
                    type="text"
                    dir="ltr"
                    value={shareUrl}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                    className="input min-w-0 flex-1 bg-gray-50 font-mono text-xs"
                  />
                  <button type="button" onClick={handleCopy} className="btn-primary shrink-0">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? t('share.copied') : t('share.copy')}
                  </button>
                </div>
              </div>

              {patientHasEmail ? (
                <div>
                  <button
                    type="button"
                    onClick={() => emailMutation.mutate()}
                    disabled={emailMutation.isPending || emailMutation.isSuccess}
                    className={`btn w-full border ${
                      emailMutation.isSuccess
                        ? 'border-success-200 bg-success-50 text-success-700 disabled:opacity-100'
                        : emailMutation.isError
                          ? 'border-danger-200 bg-danger-50 text-danger-700 hover:bg-danger-100'
                          : 'border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {emailMutation.isPending ? (
                      <>
                        <Spinner className="h-4 w-4" />
                        {t('share.sendEmail')}
                      </>
                    ) : emailMutation.isSuccess ? (
                      <>
                        <Check className="h-4 w-4" />
                        {t('share.emailSent')}
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        <span className="truncate">{t('share.sendEmailDescription')}</span>
                      </>
                    )}
                  </button>

                  {emailMutation.isError && (
                    <p className="field-error">{getApiError(emailMutation.error).message}</p>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-center text-sm text-gray-500">
                  {t('share.noPatientEmail')}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn bg-[#25D366] text-white shadow-sm hover:bg-[#1ebe5a]"
                >
                  <MessageCircle className="h-4 w-4" />
                  {t('share.sendWhatsApp')}
                </a>
                <a href={smsUrl} className="btn-secondary">
                  <MessageSquare className="h-4 w-4" />
                  {t('share.sendSMS')}
                </a>
              </div>

              <p className="text-xs text-gray-500">
                {t('share.expiresAt')}: {new Date(shareMutation.data.expiresAt).toLocaleDateString(dateLocale)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
